/**
 * DeepResearchPanel — ADR-104 v3 (T7.2) deep-research progress UX.
 *
 * Renders the lead's plan rationale + one card per parallel facet (worker),
 * keyed off the message's WorkerStatusEvent-derived `workers` map. This is the
 * headline deep-research visual ("Researching N facets in parallel") that
 * streams live and settles into a summary. Returns null when a message carries
 * neither workers nor a plan rationale, so v2 (non-deep-research) runs are
 * visually untouched.
 */

import { useEffect, useState } from "react";

import { type UiMessage, useChatStore, type WorkerState } from "@/runtime/chatStore";

const STATUS_LABEL: Record<WorkerState["status"], string> = {
  spawned: "Queued",
  searching: "Researching…",
  done: "Done",
  failed: "Failed",
};

function facetWord(n: number): string {
  return `facet${n === 1 ? "" : "s"}`;
}

function ResearchIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={active ? "text-[var(--accent-text)]" : "text-[var(--text-secondary)]"}
    >
      <circle cx="7" cy="7" r="4.25" />
      <path d="M10.2 10.2 14 14" />
    </svg>
  );
}

function WorkerDot({ status, live }: { status: WorkerState["status"]; live: boolean }) {
  if (status === "done") {
    return (
      <span
        aria-hidden="true"
        className="mt-1 inline-flex h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--status-success)]"
      />
    );
  }
  if (status === "failed") {
    return (
      <span
        aria-hidden="true"
        className="mt-1 inline-flex h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--status-error)]"
      />
    );
  }
  if (status === "searching") {
    return (
      <span aria-hidden="true" className="relative mt-1 inline-flex h-1.5 w-1.5 flex-shrink-0">
        {live && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--accent-primary)] opacity-40" />
        )}
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--accent-primary)]" />
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className="mt-1 inline-flex h-1.5 w-1.5 flex-shrink-0 rounded-full border border-[var(--text-tertiary)] bg-transparent"
    />
  );
}

function WorkerCard({ worker, live }: { worker: WorkerState; live: boolean }) {
  const isSearching = worker.status === "searching";
  const labelClass =
    isSearching && live
      ? "bk-anim-shimmer"
      : worker.status === "failed"
        ? "text-[var(--status-error)]"
        : "text-[var(--text-secondary)]";
  return (
    <li
      data-testid="worker-card"
      data-worker-id={worker.workerId}
      data-status={worker.status}
      className="flex items-start gap-2 rounded-[var(--radius-md)] bg-[var(--surface-raised)] px-[var(--space-3)] py-[var(--space-2)] shadow-[var(--elev-ring)] transition-shadow duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:shadow-[var(--elev-whisper)]"
    >
      <WorkerDot status={worker.status} live={live} />
      <div className="min-w-0 flex-1">
        <div
          className="truncate type-body-sm font-medium text-[var(--text-primary)]"
          title={worker.facet || worker.workerId}
        >
          {worker.facet || worker.workerId}
        </div>
        <div className={`mt-0.5 type-caption ${labelClass}`}>{STATUS_LABEL[worker.status]}</div>
        {worker.summary && (
          <div className="mt-1 line-clamp-2 type-caption text-[var(--text-tertiary)]">{worker.summary}</div>
        )}
      </div>
    </li>
  );
}

export interface DeepResearchPanelProps {
  message: UiMessage;
  isStreaming: boolean;
}

export function DeepResearchPanel({ message, isStreaming }: DeepResearchPanelProps) {
  const isReplaying = useChatStore((s) => s.isReplaying);
  const settled = !isStreaming || isReplaying;
  const showCollapse = settled && !!message.workers && Object.keys(message.workers).length > 0;
  const [expanded, setExpanded] = useState(!settled);

  useEffect(() => {
    setExpanded(!showCollapse);
  }, [showCollapse]);

  const workers = message.workers ? Object.values(message.workers).sort((a, b) => a.startedAt - b.startedAt) : [];
  const rationale = message.plan?.rationale?.trim() ?? "";
  const total = workers.length;
  if (total === 0 && !rationale) return null;

  const live = isStreaming && !isReplaying;
  const doneCount = workers.filter((w) => w.status === "done").length;
  const failedWorkers = workers.filter((w) => w.status === "failed");
  const activeCount = workers.filter((w) => w.status === "spawned" || w.status === "searching").length;
  const headlineActive = live && activeCount > 0;

  let headlineText: string;
  if (total === 0) {
    headlineText = live ? "Planning research…" : "Research plan";
  } else if (headlineActive) {
    headlineText = `Researching ${total} ${facetWord(total)} in parallel`;
  } else {
    headlineText = `Researched ${total} ${facetWord(total)}`;
  }

  const headlineInner = (
    <span className="flex items-center gap-2">
      <ResearchIcon active={headlineActive} />
      <span
        className={
          headlineActive
            ? "bk-anim-shimmer type-body-sm font-medium"
            : "type-body-sm font-medium text-[var(--text-primary)]"
        }
      >
        {headlineText}
      </span>
    </span>
  );

  return (
    <section
      data-testid="deep-research-panel"
      role="status"
      aria-live="polite"
      className="bk-anim-message my-3 rounded-[var(--radius-lg)] bg-[var(--surface-sunken)] p-[var(--space-3)] shadow-[var(--elev-ring)]"
    >
      {showCollapse ? (
        <button
          type="button"
          data-testid="deep-research-headline"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex w-full items-center gap-1.5 text-left transition-colors hover:opacity-80"
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className={`flex-shrink-0 text-[var(--text-tertiary)] transition-transform duration-[var(--dur-fast)] ease-[var(--ease-out)] ${expanded ? "rotate-90" : ""}`}
            aria-hidden="true"
          >
            <path d="M3 2l3 3-3 3" />
          </svg>
          {headlineInner}
        </button>
      ) : (
        <div data-testid="deep-research-headline">{headlineInner}</div>
      )}

      {rationale && (
        <p
          data-testid="deep-research-rationale"
          className="mt-[var(--space-2)] type-caption text-[var(--text-secondary)]"
        >
          {rationale}
        </p>
      )}

      {total > 0 && (
        <ul className={showCollapse && !expanded ? "sr-only" : "mt-2.5 grid grid-cols-1 gap-1.5 sm:grid-cols-2"}>
          {workers.map((w) => (
            <WorkerCard key={w.workerId} worker={w} live={live} />
          ))}
        </ul>
      )}

      {total > 0 && (
        <div
          data-testid="deep-research-progress"
          className="mt-2 flex items-center gap-2 type-caption tabular-nums text-[var(--text-tertiary)]"
        >
          <span>
            {doneCount} / {total} {facetWord(total)} complete
          </span>
          {failedWorkers.length > 0 && (
            <span className="text-[var(--status-error)]">· {failedWorkers.length} uncovered</span>
          )}
        </div>
      )}

      {failedWorkers.length > 0 && (
        <div
          data-testid="uncovered-note"
          role="note"
          className="mt-2 rounded-[var(--radius-md)] border border-[var(--status-warning)] bg-[var(--status-warning-bg)] px-[var(--space-3)] py-[var(--space-2)] type-caption text-[var(--text-secondary)]"
        >
          <span className="font-medium text-[var(--status-warning)]">
            {failedWorkers.length} {facetWord(failedWorkers.length)} uncovered:
          </span>{" "}
          {failedWorkers.map((w) => w.facet || w.workerId).join(", ")}
        </div>
      )}
    </section>
  );
}
